import React, { useState, useRef, useEffect } from "react";
import Lottie, { LottieRef } from "lottie-react";
import styled from "styled-components";

import { useIsInViewport } from "@trailhead/core-ui";

import { LottiePlayerProps } from "../utils";

const LottiePlayerContainer = styled.div<{ additionalProps: string }>`
  ${({ additionalProps }) => additionalProps};
  :hover {
    cursor: pointer;
  }
`;

export const LottiePlayer: React.FC<LottiePlayerProps> = ({
  loop = 0,
  autoplay = true,
  msLoopDelay = 0,
  playInViewport = true,
  altLabel = "",
  pauseAtFrame = 0,
  animationData,
  style = "",
  afterOnComplete,
  afterOnLoopComplete,
}) => {
  const lottieRef = useRef() as LottieRef;
  const animationItem = lottieRef.current?.animationItem;
  const ref = useRef(null);
  const inViewport = useIsInViewport(ref);
  const hasMsLoopDelay = msLoopDelay !== 0;

  const [isAnimationCompleted, setIsAnimationCompleted] =
    useState<boolean>(false);
  const [tempPauseAtFrame, setTempPauseAtFrame] =
    useState<number>(pauseAtFrame);
  const [pauseByFrame, setPauseByFrame] = useState<boolean>(false);
  const [loopTimeoutId, setLoopTimeoutId] = useState<NodeJS.Timeout>();

  useEffect(() => {
    if (pauseAtFrame === 0 && hasMsLoopDelay && animationItem?.totalFrames) {
      setTempPauseAtFrame(animationItem.totalFrames);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationItem?.totalFrames]);

  const onUndoPauseByFrame = () => {
    setTimeout(() => {
      setPauseByFrame(false);
    }, 500);
  };

  const onUndoTimeout = () => {
    clearTimeout(loopTimeoutId);
    if (pauseByFrame && loopTimeoutId) {
      setLoopTimeoutId(undefined);
      onUndoPauseByFrame();
    }
  };

  const onPlay = () => {
    lottieRef.current?.play();
  };

  const onPause = () => {
    lottieRef.current?.pause();
  };

  const onMsLoopDelayPlay = () => {
    onPlay();
    onUndoPauseByFrame();
  };

  useEffect(() => {
    if (inViewport && !autoplay && playInViewport) {
      onPlay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewport]);

  const onGotoAndPlay = (frame: number) => {
    lottieRef.current?.goToAndPlay(frame, true);
  };

  const onToggleAnimation = () => {
    if (animationItem?.isPaused) {
      if (isAnimationCompleted) {
        onGotoAndPlay(0);
        setIsAnimationCompleted(false);
      }
      if (hasMsLoopDelay) {
        onUndoTimeout();
      }
      onPlay();
    } else {
      onPause();
    }
  };

  const onComplete = () => {
    setIsAnimationCompleted(true);
    if (pauseAtFrame !== 0) {
      onGotoAndPlay(0);
      setPauseByFrame(false);
    }
    if (afterOnComplete) {
      afterOnComplete(lottieRef);
    }
  };

  const onLoopComplete = () => {
    if (afterOnLoopComplete) {
      afterOnLoopComplete(lottieRef);
    }
  };

  const minPauseFrame = tempPauseAtFrame - 2;
  const maxPauseFrame = tempPauseAtFrame + 2;

  const checkPauseFrame = () => {
    const isLastLoop = animationItem?.playCount === loop;
    const pauseCurrentLoop = hasMsLoopDelay || isLastLoop;
    const currentFrame = Math.round(animationItem?.currentFrame || 0);
    const pauseCurrentFrame =
      currentFrame >= minPauseFrame && currentFrame <= maxPauseFrame;

    return pauseCurrentLoop && pauseCurrentFrame;
  };

  const onEnterFrame = () => {
    if (tempPauseAtFrame !== 0 && !pauseByFrame) {
      if (checkPauseFrame()) {
        onPause();
        // For fixing the behavior when the animation is unpaused immediately
        setPauseByFrame(true);

        //loop delay implementation
        if (hasMsLoopDelay && !isAnimationCompleted) {
          const timeout = setTimeout(() => {
            onMsLoopDelayPlay();
          }, msLoopDelay);

          setLoopTimeoutId(timeout);
        }
      }
    }
  };

  return (
    <LottiePlayerContainer additionalProps={style}>
      <Lottie
        role="presentation"
        aria-label={altLabel}
        lottieRef={lottieRef}
        loop={loop}
        animationData={animationData}
        autoplay={autoplay}
        onClick={onToggleAnimation}
        onLoopComplete={onLoopComplete}
        onComplete={onComplete}
        onEnterFrame={onEnterFrame}
      />
      {/* Way of making sure that the Lottie has been seen until the end by the user before playing in viewport */}
      <div ref={ref} />
    </LottiePlayerContainer>
  );
};
